// Dados do totem/balcão: cardápio (produtos, categorias, promoções, turno),
// formas de pagamento do Balcão, config do cashback e logo. Verbatim do
// antigo BalcaoVendas.js.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Banknote, CreditCard, QrCode, Smartphone } from "lucide-react";
import { API, useCompanyLogo } from "../lib/helpers";
import { cashbackApi } from "../cashback";

export function useTotemDados({ autoAtendimento, autoAtendimentoRef }) {
  const companyLogo = useCompanyLogo();
  const [products, setProducts] = useState([]);
  const [allProductsForPhotos, setAllProductsForPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Cards cinza só na 1ª leitura do cardápio: o loadData depois de cada venda
  // (resetTotem) atualiza por trás, sem voltar ao carregamento.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [apiPaymentMethods, setApiPaymentMethods] = useState(null);
  const [cashbackCfg, setCashbackCfg] = useState(null);

  // ── Load data ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [prodRes, catRes, promoRes, allProdRes, turnoRes] = await Promise.all([
        // Canal de venda do modo aberto: Totem tem o seu desde 24/10/2026. O
        // Balcão (atendente) usa este mesmo hook e continua no canal Balcão —
        // com "Totem" fixo ele mostrava os produtos e preços do Totem.
        axios.get(`${API}/products/for-sale?module=${autoAtendimentoRef.current ? "Totem" : "Balcao"}`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/promotions/active?module=Balcao`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, { headers }).catch(() => ({ data: [] })),
        // TURNO: só o modo AUTOATENDIMENTO segue o turno. Nele quem se serve é
        // o cliente, então vale a mesma regra do cardápio web. No modo operado
        // por atendente o Balcão continua sendo PDV e vê o catálogo inteiro.
        autoAtendimentoRef.current
          ? axios.get(`${API}/public/cardapio-profile/active`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      let disponiveis = prodRes.data.filter((p) => p.available !== false);
      const cats = turnoRes?.data?.categorias_nomes || [];
      if (cats.length > 0) {
        const permitidas = new Set(cats);
        // Produto sem categoria sempre passa — mesma regra do resto do turno.
        disponiveis = disponiveis.filter(
          (p) => !String(p.category || '').trim() || permitidas.has(String(p.category).trim())
        );
      }
      setProducts(disponiveis);
      setAllProductsForPhotos(Array.isArray(allProdRes.data) ? allProdRes.data : []);
      setCategories(catRes.data || []);
      setPromotions(promoRes.data || []);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      toast.error("Erro ao carregar cardápio");
    } finally {
      setLoading(false);
      setPrimeiraCarga(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Carregar formas de pagamento da API ──
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const iconMap = { dinheiro: Banknote, pix: QrCode, pix_online: QrCode, cartao_online: CreditCard, credito: CreditCard, debito: Smartphone, cartao: CreditCard };
    // Totem e Balcão têm chave própria na forma de pagamento (26/10/2026)
    axios.get(`${API}/payment-methods/module/${autoAtendimento ? "totem" : "balcao"}`, { headers })
      .then(res => {
        const pms = Array.isArray(res.data) ? res.data : [];
        // Lista vazia volta ao padrão (null) — sem isso, trocar Balcão ↔ Totem
        // deixava na tela as formas do outro modo
        setApiPaymentMethods(pms.length ? pms.map(pm => ({
          id: pm.code || pm.id,
          code: pm.code,
          label: pm.name || pm.code,
          icon: iconMap[(pm.code || "").toLowerCase()] || CreditCard,
          isCash: (pm.code || "").toLowerCase() === "dinheiro",
        })) : null);
      })
      .catch(() => {}); // fallback para hardcoded
  }, [autoAtendimento]);

  // Config do cashback (auth). Recarrega ao trocar de modo — a regra é por módulo.
  useEffect(() => {
    let vivo = true;
    cashbackApi.config().then((r) => { if (vivo) setCashbackCfg(r.data || null); }).catch(() => { if (vivo) setCashbackCfg(null); });
    return () => { vivo = false; };
  }, [autoAtendimento]);

  return { companyLogo, products, allProductsForPhotos, categories, promotions, loading, primeiraCarga, loadData, apiPaymentMethods, cashbackCfg };
}
